import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import io from "socket.io-client";
import { deleteCommentOfDay, deleteTipOfDay, getCommentsForAdminOfDay, updateTipOfDay } from "../../service/service";
import { BASE_URL } from "../../service/config";
import ConfirmationModal from "../ConfirmationModal";
import CommentsLightbox from "./CommentsLightbox";
import CommentThread from "./CommentThread";
import FullHistoryModal from "./FullHistoryModal";
import AudioPlayerComponent from "../AudioPlayer";
import { COLORS, REACTION_COLORS, TYPE_META } from "./colors";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import StarIcon from "@mui/icons-material/Star";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TollIcon from "@mui/icons-material/Toll";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

const REWARD_OPTIONS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];

// Elijah's exact required copy (Stage 3 spec, 2026-08-03).
const LIVE_EDIT_WARNING =
  "This tip is currently live. Any changes you save will be shown to users immediately. Are you sure you want to continue?";
const LIVE_STATUS_GREEN = REACTION_COLORS.insightful.color;

// Matches the backend's accepted extensions/size cap for tip audio uploads.
const MAX_AUDIO_SIZE = 50 * 1024 * 1024;
const ACCEPTED_AUDIO_EXTENSIONS = ["mp3", "wav"];

// Reaction/comment counts and history come from real backend fields — all genuinely 0 right
// now since there's no reactions/comments/view-tracking feature built yet. Not placeholders.
const REACTION_META = [
  { key: "insightfulCount", label: "Insightful", icon: ThumbUpIcon, ...REACTION_COLORS.insightful },
  { key: "interestingCount", label: "Interesting", icon: StarIcon, ...REACTION_COLORS.interesting },
  { key: "helpfulCount", label: "Helpful", icon: LightbulbIcon, ...REACTION_COLORS.helpful },
];

const formatDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getUTCFullYear()}`;
};

const HeaderStat = ({ label, children }) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const FooterStat = ({ icon, value, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {icon}
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Box>
);

const CurrentLiveTip = ({ tip, onStructureChanged, onStatsChanged }) => {
  const [mode, setMode] = useState("view");
  const [expanded, setExpanded] = useState(true);
  const [title, setTitle] = useState(tip.title);
  const [description, setDescription] = useState(tip.description);
  const [reward, setReward] = useState(tip.reward);
  const [newAudioFile, setNewAudioFile] = useState(null);
  const [audioFileError, setAudioFileError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, focusCommentId: null });
  const [historyOpen, setHistoryOpen] = useState(false);
  const typeMeta = TYPE_META[tip.type];
  const isEditing = mode === "edit";
  const isExpanded = isEditing || expanded;

  const latestDates = [...(tip.shownHistory || [])]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-3);

  const fetchComments = useCallback(async () => {
    setCommentsLoading(true);
    const result = await getCommentsForAdminOfDay(tip._id);
    if (result?.success) {
      setComments(result.data || []);
    }
    setCommentsLoading(false);
  }, [tip._id]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Comment create/reply/delete all change the tip's commentsCount server-side — refetching just
  // the comment list would leave the header stat and "Comments (N)" heading showing a stale count.
  const refreshAfterCommentChange = useCallback(() => {
    fetchComments();
    onStatsChanged?.();
  }, [fetchComments, onStatsChanged]);

  // Held in a ref so the socket effect below can stay keyed on tip._id alone. Depending on the
  // callback directly would reconnect the socket on every render, since callbacks may be passed as
  // an inline arrow from TipOfDay.js and so changes identity each time the parent re-renders.
  const refreshAfterCommentChangeRef = useRef(refreshAfterCommentChange);
  refreshAfterCommentChangeRef.current = refreshAfterCommentChange;

  // Live comments: the backend broadcasts "tipOfDayCommentsChanged" to every connected client
  // whenever a comment or reply changes, so a user commenting from the mobile app appears here
  // without a manual refresh. The event carries only the tipId, so we re-fetch on receipt.
  // Reaction counts come off the `tip` prop, so refreshing them just means asking the parent to
  // refetch — no comment refetch needed, hence its own ref rather than reusing the one above.
  const onStatsChangedRef = useRef(onStatsChanged);
  onStatsChangedRef.current = onStatsChanged;

  useEffect(() => {
    const socket = io(BASE_URL, { transports: ["websocket"] });
    socket.on("tipOfDayCommentsChanged", (payload) => {
      if (payload?.tipId === tip._id) {
        refreshAfterCommentChangeRef.current();
      }
    });

    // A user reacting from the mobile app updates the Insightful/Interesting/Helpful counts
    // shown here without a manual refresh.
    socket.on("tipOfDayReactionsChanged", (payload) => {
      if (payload?.tipId === tip._id) {
        onStatsChangedRef.current?.();
      }
    });

    // A user opening the tip in the mobile app updates Total views / Shown X times / Last shown
    // here without a manual refresh.
    socket.on("tipOfDayViewsChanged", (payload) => {
      if (payload?.tipId === tip._id) {
        onStatsChangedRef.current?.();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [tip._id]);

  const handleDeleteComment = async (commentId) => {
    await deleteCommentOfDay(commentId);
    refreshAfterCommentChange();
  };

  const handleStartEdit = () => {
    setError("");
    setMode("edit");
  };

  const handleCancel = () => {
    setTitle(tip.title);
    setDescription(tip.description);
    setReward(tip.reward);
    setNewAudioFile(null);
    setAudioFileError("");
    setError("");
    setMode("view");
  };

  const handleAudioFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_AUDIO_EXTENSIONS.includes(extension)) {
      setAudioFileError("Only MP3 and WAV files are accepted.");
      return;
    }
    if (file.size > MAX_AUDIO_SIZE) {
      setAudioFileError("Audio file must be under 50MB.");
      return;
    }

    setAudioFileError("");
    setNewAudioFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    const result = await updateTipOfDay(tip._id, {
      title,
      description,
      type: tip.type,
      reward,
      publishDate: tip.publishDate,
      audioFile: newAudioFile,
    });
    setSaving(false);

    if (result?.success === false) {
      setError(result.error?.message || result.error?.err || "Failed to save changes. Please try again.");
      return;
    }

    setNewAudioFile(null);
    setAudioFileError("");
    setMode("view");
    onStructureChanged?.();
  };

  const handleDeleteTip = async () => {
    setDeleting(true);
    const result = await deleteTipOfDay(tip._id);
    setDeleting(false);
    if (result?.success !== false) {
      onStructureChanged?.();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.primaryText, mb: 1.5 }}>
        Current Tip (Live)
      </Typography>

      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, flexWrap: "wrap" }}>
          <Avatar sx={{ bgcolor: typeMeta.color, width: 36, height: 36 }}>
            {tip.type === "text" ? "T" : "A"}
          </Avatar>
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            <Chip
              label={typeMeta.label}
              size="small"
              sx={{
                bgcolor: typeMeta.bg,
                color: typeMeta.color,
                fontWeight: 600,
                borderRadius: 1,
                "& .MuiChip-label": { padding: "4px 12px" },
              }}
            />
            <HeaderStat label="Reward">
              {isEditing ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Select
                    size="small"
                    value={reward}
                    onChange={(event) => setReward(event.target.value)}
                    sx={{ minWidth: 88 }}
                  >
                    {REWARD_OPTIONS.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  <TollIcon sx={{ fontSize: 18, color: COLORS.gold }} />
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
                  <Typography sx={{ fontWeight: 700, color: COLORS.primaryText }}>{reward}</Typography>
                  <TollIcon sx={{ fontSize: 18, color: COLORS.gold }} />
                </Box>
              )}
            </HeaderStat>
            <HeaderStat label="Live today">
              <Typography sx={{ fontWeight: 700, color: COLORS.primaryText }}>{formatDate(tip.publishDate)}</Typography>
            </HeaderStat>
            <HeaderStat label="Views">
              <Typography sx={{ fontWeight: 700 }}>{tip.views}</Typography>
            </HeaderStat>
            <HeaderStat label="Rotations">
              <Typography sx={{ fontWeight: 700 }}>{tip.rotations}</Typography>
            </HeaderStat>
            <HeaderStat label="Comments">
              <Typography sx={{ fontWeight: 700 }}>{tip.commentsCount}</Typography>
            </HeaderStat>
            <HeaderStat label="Reactions">
              <Typography sx={{ fontWeight: 700 }}>{tip.reactionsCount}</Typography>
            </HeaderStat>

            {isEditing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  sx={{ color: COLORS.primaryText, borderColor: COLORS.primaryText }}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ConfirmationModal
                  title="Edit live tip?"
                  description={LIVE_EDIT_WARNING}
                  confirmLabel="Continue Editing"
                  cancelLabel="Cancel"
                  showCloseButton
                  onConfirm={handleStartEdit}
                  renderTrigger={({ openModal }) => (
                    <Button variant="outlined" size="small" sx={{ color: COLORS.link, borderColor: COLORS.link }} onClick={openModal}>
                      Edit
                    </Button>
                  )}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      mt: 2,
                      mb: 1,
                      border: "1px solid",
                      borderColor: COLORS.border,
                      borderRadius: 1.5,
                    }}
                  >
                    <Avatar sx={{ bgcolor: typeMeta.color, width: 36, height: 36 }}>
                      {tip.type === "audio" ? <VolumeUpIcon sx={{ fontSize: 18 }} /> : "T"}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{tip.title}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Chip
                          label={typeMeta.label}
                          size="small"
                          sx={{
                            bgcolor: typeMeta.bg,
                            color: typeMeta.color,
                            fontWeight: 600,
                            borderRadius: 1,
                            "& .MuiChip-label": { padding: "2px 10px" },
                          }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: LIVE_STATUS_GREEN }} />
                          <Typography variant="body2" sx={{ color: LIVE_STATUS_GREEN, fontWeight: 600 }}>
                            Live today
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ConfirmationModal>
                <ConfirmationModal
                  title="Delete this tip?"
                  description={`"${tip.title}" will be permanently deleted. This can't be undone.`}
                  confirmLabel="Delete"
                  cancelLabel="Cancel"
                  loadingLabel="Deleting…"
                  loading={deleting}
                  destructive
                  onConfirm={handleDeleteTip}
                  renderTrigger={({ openModal, loading }) => (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ color: COLORS.destructive, borderColor: COLORS.destructive }}
                      onClick={openModal}
                      disabled={loading}
                    >
                      {loading ? "Deleting…" : "Delete"}
                    </Button>
                  )}
                />
              </Box>
            )}
            <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} disabled={isEditing}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" sx={{ color: COLORS.destructive, px: 2, pb: 1 }}>
            {error}
          </Typography>
        )}

        {isExpanded && (
          <>
            <Divider />

            <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: "1 1 300px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Title
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {title}
                  </Typography>
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Description
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                ) : (
                  <Typography variant="body2">{description}</Typography>
                )}

                {tip.type === "audio" && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Audio
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5, flexWrap: "wrap" }}>
                      <Typography variant="body2" color="text.secondary">
                        {newAudioFile ? `${newAudioFile.name} (replaces existing on save)` : tip.audioFileName}
                      </Typography>
                      {isEditing && (
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          sx={{ color: COLORS.link, borderColor: COLORS.link }}
                        >
                          Change Audio
                          <input type="file" accept="audio/*" hidden onChange={handleAudioFileChange} />
                        </Button>
                      )}
                    </Box>
                    {audioFileError && (
                      <Typography variant="body2" sx={{ color: COLORS.destructive, mb: 0.5 }}>
                        {audioFileError}
                      </Typography>
                    )}
                    <AudioPlayerComponent audioUrl={tip.audioUrl} />
                  </Box>
                )}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

              <Box sx={{ flex: "1 1 340px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Comments ({tip.commentsCount})
                </Typography>
                {commentsLoading && <CircularProgress size={20} />}
                {!commentsLoading && comments.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No comments yet.
                  </Typography>
                )}
                {!commentsLoading &&
                  comments.slice(0, 2).map((comment) => (
                    <CommentThread
                      key={comment._id}
                      comment={comment}
                      onReply={(selected) => setLightbox({ open: true, focusCommentId: selected._id })}
                      onDelete={handleDeleteComment}
                    />
                  ))}
                {!commentsLoading && comments.length > 0 && (
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    sx={{ color: COLORS.link }}
                    onClick={() => setLightbox({ open: true, focusCommentId: null })}
                  >
                    View all {tip.commentsCount} comments
                  </Link>
                )}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

              <Box sx={{ flex: "0 1 220px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Reactions
                </Typography>
                {REACTION_META.map((reaction) => {
                  const ReactionIcon = reaction.icon;
                  return (
                    <Box
                      key={reaction.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        bgcolor: reaction.bg,
                        borderRadius: 1,
                        p: 1.5,
                        mb: 2,
                      }}
                    >
                      <Avatar sx={{ bgcolor: reaction.color, width: 28, height: 28 }}>
                        <ReactionIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {reaction.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {tip[reaction.key] || 0}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap" }}>
              <Box
                sx={{
                  flex: "1 1 300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                  flexWrap: "wrap",
                }}
              >
                <FooterStat icon={<VisibilityIcon color="action" />} value={tip.views} label="total views" />
                <FooterStat icon={<AutorenewIcon color="action" />} value="Shown" label={`${tip.rotations} times`} />
                <FooterStat icon={<CalendarTodayIcon color="action" />} value="Last shown" label={formatDate(tip.lastShownDate) || "Never"} />
              </Box>

              <Box
                sx={{
                  flex: "1 1 560px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Latest dates:
                  </Typography>
                  {latestDates.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Not shown yet
                    </Typography>
                  )}
                  {latestDates.map((entry) => (
                    <Typography key={entry.date} variant="body2">
                      {formatDate(entry.date)} — {entry.viewsCount} views
                    </Typography>
                  ))}
                </Box>
                <Button variant="outlined" sx={{ color: COLORS.link, borderColor: COLORS.link }} onClick={() => setHistoryOpen(true)}>
                  View Full History
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <CommentsLightbox
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, focusCommentId: null })}
        tipId={tip._id}
        tipTitle={tip.title}
        comments={comments}
        focusCommentId={lightbox.focusCommentId}
        onCommentsChanged={refreshAfterCommentChange}
      />

      <FullHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} tip={tip} />
    </Box>
  );
};

export default CurrentLiveTip;
